import { useTranslations } from 'next-intl';
import { type FC } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';

import clsx from 'clsx';
import { z } from 'zod';

import { FormSelect } from '@/components/common/form/FormSelect';
import { FormSubmitButton } from '@/components/common/form/FormSubmitButton';
import { CurrencySelect } from '@/components/common/select/CurrencySelect';
import { OperationPeriodSelect } from '@/components/common/select/OperationPeriodSelect';
import { OperationTypeSelect } from '@/components/common/select/OperationTypeSelect';
import { type WithClassName } from '@/types/react';

import {
  DashboardV2PeriodType,
  SalesChartOperationType,
} from '../../../../../../../../../../../../../generated/graphql';

import styles from './SalesChartFilter.module.scss';

type SalesChartFilterProps = WithClassName & {
  defaultValues?: Partial<SalesChartFilter>;
  isInGate: boolean;
  onSubmit: SubmitHandler<SalesChartFilter>;
};

const useFormSchema = () => {
  return z.object({
    period: z.nativeEnum(DashboardV2PeriodType),
    currency: z.object({
      id: z.string(),
      name: z.string(),
    }),
    operationType: z.nativeEnum(SalesChartOperationType),
  });
};

export type SalesChartFilter = z.infer<ReturnType<typeof useFormSchema>>;

export const SalesChartFilter: FC<SalesChartFilterProps> = (props) => {
  const t = useTranslations('charts.filters');
  const tButtons = useTranslations('buttons');

  const { className, defaultValues, isInGate, onSubmit } = props;

  const form = useForm<SalesChartFilter>({
    resolver: zodResolver(useFormSchema()),
    defaultValues,
  });

  const { handleSubmit, register } = form;

  return (
    <FormProvider {...form}>
      <form className={clsx(styles.root, className)} onSubmit={handleSubmit(onSubmit)}>
        <FormSelect {...register('period')} variant="box" label={t('period.label')} component={OperationPeriodSelect} />

        {!isInGate && (
          <FormSelect {...register('currency')} variant="box" label={t('currency.label')} component={CurrencySelect} />
        )}

        <FormSelect
          {...register('operationType')}
          variant="box"
          label={t('operationType.label')}
          component={OperationTypeSelect}
          withAll
        />

        <FormSubmitButton className={styles.submitButton}>{tButtons('search')}</FormSubmitButton>
      </form>
    </FormProvider>
  );
};
